// LE MESSAGE D'INVITATION : une proposition écrite d'après le contexte, ou un
// texte libre.
//
// La proposition reprend qui invite, le projet, le client, l'étude, la durée
// et le FORMAT : à distance (un endroit calme, un micro ou un casque, l'écran
// qu'on peut partager), sur place (le lieu), ou au choix de la personne. Tant
// qu'on ne l'a pas retouchée, elle suit les changements (durée, formats) ;
// retouchée, elle reste telle quelle.
//
// L'e-mail commence déjà par « Bonjour <prénom> » : le message enchaîne.
import { useEffect, useState } from 'react';
import { useBootstrap, useMe } from '../api/queries';
import { TextArea } from '../write/Forms';
import type { Format } from './SlotPicker';
import { Button } from '../components/ui';

export interface MessageContext {
  organizer: string;
  projectName: string;
  clientName: string;
  /** L'objectif de l'étude, ou son code. */
  studyLabel: string;
  durationMin: number;
  formats: Format[];
  location: string;
}

/** La proposition de message. PUR. */
export function proposedMessage(c: MessageContext): string {
  const remote = c.formats.filter((f) => f !== 'onsite');
  const onsite = c.formats.includes('onsite');
  const who = c.organizer ? `Je suis ${c.organizer}, UX Researcher.` : 'Je suis UX Researcher.';
  const product = [c.projectName && `de ${c.projectName}`, c.clientName && `pour ${c.clientName}`].filter(Boolean).join(' ');

  const lines: string[] = [
    `${who} Je m’occupe des entretiens avec les utilisateurs${product ? ` ${product}` : ''}.`,
    '',
    c.studyLabel
      ? `Dans le cadre de l’étude « ${c.studyLabel} », j’aimerais échanger avec vous pendant ${c.durationMin} minutes.`
      : `J’aimerais échanger avec vous pendant ${c.durationMin} minutes.`,
  ];

  if (remote.length > 0 && onsite) {
    lines.push('Je vous invite à choisir le créneau qui vous convient, et à me dire si vous préférez échanger en visioconférence ou nous retrouver sur place. Vous recevrez ensuite une confirmation.');
  } else if (onsite) {
    lines.push('Je vous invite à choisir le créneau qui vous convient : vous recevrez ensuite une confirmation.');
  } else {
    lines.push('Je vous invite à choisir le créneau qui vous convient : vous recevrez ensuite une confirmation avec le lien de la visioconférence.');
  }

  lines.push('', 'Vous n’avez rien à préparer.');
  if (remote.length > 0) {
    lines.push(`${onsite ? 'À distance, trouvez' : 'Trouvez'} simplement un endroit calme pour que nous puissions échanger tous les deux, et munissez-vous d’un micro ou d’un casque. Vous pourrez aussi partager votre écran si vous le souhaitez.`);
  }
  if (onsite) {
    lines.push(c.location
      ? `${remote.length > 0 ? 'Sur place, le rendez-vous a lieu' : 'Le rendez-vous a lieu'} ${c.location}.`
      : 'Le lieu du rendez-vous vous sera précisé.');
  }

  lines.push(
    '',
    `L’idée est de parler de vous, de ce que vous faites au quotidien${c.projectName ? ` et de votre usage de ${c.projectName}` : ''}. Il n’y a ni bonne ni mauvaise réponse : c’est votre expérience qui nous intéresse.`,
    '',
    'Merci d’avance pour votre temps,',
    c.organizer || '',
  );
  return lines.join('\n').trim();
}

/** Le contexte connu de l'appli : qui invite, le projet, son client. */
export function useMessageContext(projectId: string) {
  const me = useMe();
  const boot = useBootstrap(true);
  const client = boot.data?.clients.find((c) => c.projets.some((p) => p.id === projectId));
  const project = client?.projets.find((p) => p.id === projectId);
  return {
    organizer: me.data?.user.name ?? '',
    projectName: project?.nom ?? '',
    clientName: client?.nom ?? '',
  };
}

/**
 * Le champ « Message » : « Message proposé » (écrit d'après le contexte, à
 * retoucher) ou « Texte libre » (vide). `value`/`onChange` portent le texte envoyé.
 */
export function InvitationMessage({
  projectId,
  studyLabel,
  durationMin,
  formats,
  location,
  value,
  onChange,
}: {
  projectId: string;
  studyLabel: string;
  durationMin: number;
  formats: Format[];
  location: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const ctx = useMessageContext(projectId);
  const [mode, setMode] = useState<'propose' | 'libre'>('propose');
  // Retouché à la main : la proposition ne l'écrase plus.
  const [touched, setTouched] = useState(false);
  const proposal = proposedMessage({ ...ctx, studyLabel, durationMin, formats, location });

  useEffect(() => {
    if (mode === 'propose' && !touched && value !== proposal) onChange(proposal);
  }, [mode, touched, proposal, value, onChange]);

  return (
    <div className="zy-field">
      <fieldset className="inv-choice">
        <legend className="zy-field__label">Message</legend>
        <label className="ppl-check">
          <input className="zy-radio" type="radio" name="inv-msg" checked={mode === 'propose'} onChange={() => { setMode('propose'); setTouched(false); }} />
          Message proposé
        </label>
        <label className="ppl-check">
          <input className="zy-radio" type="radio" name="inv-msg" checked={mode === 'libre'} onChange={() => { setMode('libre'); setTouched(true); onChange(''); }} />
          Texte libre
        </label>
      </fieldset>
      <TextArea
        label={mode === 'propose' ? 'Message proposé (modifiable)' : 'Votre message'}
        value={value}
        onChange={(v) => { setTouched(true); onChange(v); }}
        rows={mode === 'propose' ? 12 : 6}
        placeholder="Le déroulé, ce qu’il faut prévoir, l’enregistrement éventuel de l’échange."
        hint={mode === 'propose'
          ? (touched
            ? 'Vous l’avez retouché : il ne suit plus les changements de durée ou de format.'
            : 'Écrit d’après le projet, l’étude, la durée et les formats cochés ; il suit leurs changements.')
          : 'Il s’affiche dans l’e-mail et sur la page de la personne, sous « Bonjour <prénom> ».'}
      />
      {mode === 'propose' && touched ? (
        <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => { setTouched(false); onChange(proposal); }}>
          Revenir au message proposé
        </Button>
      ) : null}
    </div>
  );
}
